import JSZip from "jszip";
import { General } from "@/utils/constants";
import { SnackToast as Toast } from "@/utils/snackToast";
import { XhrUploadEvent } from "./utils-types";
import { IDownloadFileQueue } from "@/store/types/file-type";
import { MINE_TYPE } from "@/utils/enumerate";

export const getExtension = (fileName: string) =>
  /.{10,300}\.(\w+)/.exec(fileName)?.[1];

export const getFileNameS3 = (url: string) => {
  const nameMatch = url.match(/(\w{1,90}\.\w{2,5})(?:\?|$)/);
  return nameMatch ? nameMatch[1] : null;
};

export const normalizeName = (document: IDownloadFileQueue) => {
  const URL = document.file || document.url_file;
  const FILE = (!URL.includes("http") ? "http://127.0.0.1:8887/" : "") + URL;
  return `${document.attachment_name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\W/g, "_")}_${document.id ||
    document.process_file_id}.${getExtension(FILE)}`;
};

export const downloadFile = (fileObject: IDownloadFileQueue) => {
  const anchor = document.createElement("a");
  anchor.href = fileObject.url;
  anchor.target = "_blank";
  anchor.download = fileObject.attachment_name;
  anchor.click();
};

export const downloadFileBlob = async (fileObject: IDownloadFileQueue) =>
  await fetch(fileObject.url)
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileObject.attachment_name;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    });

export const downloadBlobFile = (content: Blob, name: string) => {
  const url = URL.createObjectURL(content);
  downloadFile({
    url,
    attachment_name: name
  } as IDownloadFileQueue);
  setTimeout(() => URL.revokeObjectURL(url), 1e3);
};

export const downloadWithURL = (
  url: string,
  fileName: string,
  extension = "pdf"
) =>
  downloadFile({
    url,
    attachment_name: `${fileName}.${extension}`
  } as IDownloadFileQueue);

/**
 * @param {String} url
 * @param {File} file
 * @return {String} name_file.ext
 */
export const uploadWithURL = (
  url: string,
  file: File,
  callback = (_callback: XhrUploadEvent) => {},
  error = (_event: Event) => {}
): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function(evt: ProgressEvent<FileReader>) {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);

      xhr.onreadystatechange = function() {
        if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
          const name = getFileNameS3(url);
          name && resolve(name);
        }
      };

      const defaultCallback = (
        event: ProgressEvent<XMLHttpRequestEventTarget>
      ) => callback({ event, xhr });

      xhr.upload.addEventListener("loadstart", defaultCallback);
      xhr.upload.addEventListener("progress", defaultCallback);
      xhr.upload.addEventListener("error", error);
      xhr.upload.addEventListener("abort", error);
      xhr.send(evt.target?.result);
    };

    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

export const exportFiles = async (
  documentList: IDownloadFileQueue[],
  editable = false
) => {
  const files: Record<string, JSZip> = {},
    count: Record<string, number> = {};
  await Promise.all(
    documentList.map(async document => {
      const nameOrgan = document.origin.entity.abbreviation;
      const FILE = editable ? document.file_editable : document.file;

      if (!FILE) {
        Toast.open({
          message: General.MESSAGES.EMPTY_DOCUMENT,
          type: "is-danger"
        });
        return;
      }

      if (!files[nameOrgan]) {
        count[nameOrgan] = 0;
        files[nameOrgan] = new JSZip();
      }

      const blobData = await fetch(FILE).then(response => response.blob());
      const nameFile = document.report
        ? document.name
        : `${document.document_type}_${document.process_nup ||
            document.nup}_${++count[nameOrgan]}.${getExtension(FILE)}`;

      files[nameOrgan].file(nameFile, blobData, { binary: true });
    })
  );

  await Promise.all(
    Object.entries(files).map(
      async zip =>
        await zip[1].generateAsync({ type: "blob" }).then(function(content) {
          downloadBlobFile(content, zip[0] + ".zip");
        })
    )
  );
};

export const exportFilesPDF = async (
  documents: IDownloadFileQueue[],
  name: string
) => {
  const filesZIP = new JSZip();
  await Promise.all(
    documents.map(async document => {
      const URL = document.file || document.url_file;
      const FILE =
        (!URL.includes("http") ? "http://127.0.0.1:8887/" : "") + URL;

      if (!FILE) {
        Toast.open({
          message: General.MESSAGES.EMPTY_DOCUMENT,
          type: "is-danger"
        });
        return;
      }

      const blobData = await fetch(FILE).then(response => response.blob());
      const nameFile = normalizeName(document);

      filesZIP.file(nameFile, blobData, { binary: true });
    })
  );
  await filesZIP.generateAsync({ type: "blob" }).then(function(content) {
    downloadBlobFile(content, name + ".zip");
  });
};

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

export const base64ToFile = (base64String: string, fileName: string) => {
  const binaryString = atob(base64String);
  const length = binaryString.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new File([bytes], fileName);
};

export const getFileMimeType = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onloadend = function(event: ProgressEvent<FileReader>) {
      const result = event.target?.result;
      if (result && typeof result !== "string") {
        const arr = new Uint8Array(result).subarray(0, 4);
        let header = "";

        for (const value of arr) {
          header += value.toString(16);
        }

        const mimeType = MINE_TYPE[header as keyof typeof MINE_TYPE] || header;
        resolve(mimeType);
      } else {
        reject(new Error("Falha ao ler o arquivo"));
      }
    };

    fileReader.onerror = function() {
      reject(new Error("Erro ao ler o arquivo"));
    };

    fileReader.readAsArrayBuffer(file);
  });
};
